import React from "react";

/**
 * Wrapper component to ensure consistent styling across screens
 * Provides the same padding and layout structure as other flow screens
 */
const OwnliScreenWrapper = ({ children, className = "" }) => {
  return (
    <div className={`space-y-8 p-4 pt-5 pb-6 stagger-children ${className}`}>
      {children}
    </div>
  );
};

/**
 * Header component with consistent styling for screens
 */
export const OwnliHeader = ({
  title,
  subtitle,
  icon,
  children,
  className = "",
  iconClass = "",
  titleClass = "",
  subTitleClass = "",
}) => {
  return (
    <div
      className={`text-center p-5 rounded-[20px] space-y-4 shadow-card ${className}`}
    >
      {icon && (
        <div
          className={`w-16 h-16 bg-blue-600/10 rounded-full flex items-center justify-center mx-auto ${iconClass}`}
        >
          {icon}
        </div>
      )}
      {title && (
        <h1
          className={`text-2xl font-bold text-gray-900 font-display tracking-tight ${titleClass}`}
        >
          {title}
        </h1>
      )}
      {subtitle && (
        <p className={`text-lg text-gray-600 ${subTitleClass}`}>{subtitle}</p>
      )}
      {children}
    </div>
  );
};

/**
 * Card component with consistent styling for screens
 */
export const OwnliCard = ({
  children,
  variant = "default",
  className = "",
  withHoverEffect = true,
  ...rest
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case "primary":
        return "bg-blue-600/10 border-none rounded-[20px]";
      case "info":
        return "bg-blue-50 border-none rounded-[20px]";
      case "success":
        return "bg-green-50 border-none rounded-[20px]";
      case "error":
        return "bg-red-50 border-none rounded-[20px]";
      case "outline":
        return `border border-gray-200 !text-gray-700 border-blue-600 bg-white rounded-[20px] ${
          withHoverEffect
            ? "hover:bg-blue-600/5 hover:border-blue-600 hover:-translate-y-0.5 hover:shadow-card-hover"
            : ""
        }`;
      default:
        return "bg-white border-none rounded-[20px] shadow-card";
    }
  };

  return (
    <div
      className={`${getVariantStyles()} transition-all duration-300 ${className}`}
      {...rest}
    >
      <div className="p-5">{children}</div>
    </div>
  );
};

/**
 * Button component with consistent styling for screens
 */
export const OwnliButton = ({
  children,
  onClick,
  variant = "primary",
  disabled = false,
  className = "",
  ...props
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case "primary":
        return "bg-crimsonGlory hover:bg-blue-700 hover:shadow-glow-lg hover:-translate-y-0.5 disabled:bg-gray-400 active:scale-[0.97] active:shadow-sm";
      case "secondary":
        return "bg-gray-600 hover:bg-gray-700 active:scale-[0.97]";
      case "outline":
        return "border border-gray-300 !text-gray-700 hover:bg-gray-50 hover:border-crimsonGlory/40 active:scale-[0.97]";
      default:
        return "bg-blue-600 hover:bg-blue-700 active:scale-[0.97]";
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex uppercase font-bold items-center justify-center gap-2 text-white px-4 py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${getVariantStyles()} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

export default OwnliScreenWrapper;
